import * as joint from "@clientio/rappid";
import { State, StateView } from "../../mvc/state";
import { Transition, TransitionView } from "../../mvc/transition";
import { BaseNode } from "../../mvc/base/base-node";
import { Region } from "../../mvc/region";
import { Entry } from "../../mvc/entry";
import RappidContext from "../../rappid/rappid-context";
import { DirectedGraph } from "@joint/layout-directed-graph";
import { dia } from "@joint/core";
import { LabeledNode } from "../../mvc/base/labeled-node";

export class FallbackGraphLayouter {
  layout(context: RappidContext) {
    context.commandManager.initBatchCommand();
    const graph: dia.Graph = context.graph;
    const preprocessedLabeledNodes = this.preprocessLabeledNodes(graph);
    context.graph.getCells().forEach((cell) => {
      const view = cell.findView(context.paper);
      if (view instanceof StateView) {
        if (!view.aceEditor) {
          view.update();
        }
        view.fitToContent();
      }
      if (view instanceof TransitionView) {
        view.update();
      }
    });
    const topLevelElements = context.graph.getElements().filter((el) => {
      return !el.getParentCell();
    });
    this.layoutNodesOnSameLevel(null, topLevelElements, context, 0);
    this.adjustLabelPositions(preprocessedLabeledNodes, context);

    context.graph.getLinks().forEach((link) => {
      const view = link.findView(context.paper) as TransitionView;
      view?.update();
    });
    context.commandManager.storeBatchCommand();
  }

  private adjustLabelPositions(
    preprocessedLabeledNodes: {
      cell: LabeledNode;
      children: { child: dia.Element; dx: number; dy: number }[];
    }[],
    context: RappidContext
  ) {
    preprocessedLabeledNodes.forEach(({ cell, children }) => {
      if (children) {
        children.forEach((c) => {
          c.child.position(c.dx, c.dy, { parentRelative: true });
          const view = context.paper.findViewByModel(c.child);
          if (view && "render" in view) {
            (view as dia.ElementView).render();
          }
        });
      }
    });
  }

  /* For some reason, the order of the elements is considered by DirectedGraph => Entries shall go first, Synchronizations last. */
  private preSortElements(elements: dia.Element<dia.Element.Attributes, dia.ModelSetOptions>[]) {
    return elements.sort((a, b) => {
      const aIsEntry = a instanceof Entry;
      const bIsEntry = b instanceof Entry;
      const aIsSync = a.get("type") === "synchronization";
      const bIsSync = b.get("type") === "synchronization";

      // Entries first
      if (aIsEntry && !bIsEntry) return -1;
      if (!aIsEntry && bIsEntry) return 1;

      // Synchronizations last
      if (aIsSync && !bIsSync) return 1;
      if (!aIsSync && bIsSync) return -1;

      return 0;
    });
  }

  private layoutNodesOnSameLevel(
    parent: dia.Cell,
    elementsOnSameLevel: dia.Element<dia.Element.Attributes, dia.ModelSetOptions>[],
    context: RappidContext,
    level: number
  ) {
    if (elementsOnSameLevel.length === 0) return;
    elementsOnSameLevel = this.preSortElements(elementsOnSameLevel);
    if (elementsOnSameLevel.length > 1) level++;
    const parentIsState = parent instanceof State;

    // This is a trick for states containing orthogonal regions.
    // We want to enforce "TB" in each region, so we increase the level
    if (parentIsState && level % 2 === 1) {
      level++;
    }
    elementsOnSameLevel.forEach((el) => {
      this.layoutNodesOnSameLevel(
        el,
        el.getEmbeddedCells().filter((c) => c.isElement()) as dia.Element[],
        context,
        level
      );
    });

    const beforePositions = new Map<dia.Cell.ID, { x: number; y: number }>();
    elementsOnSameLevel.forEach((el) => {
      beforePositions.set(el.id, el.position());
    });
    const elementIDs = new Set(elementsOnSameLevel.map((el) => el.id));
    const linksOnSameLevel: dia.Link[] = [];

    context.graph.getLinks().forEach((link) => {
      const sourceId = link.get("source")?.id;
      const targetId = link.get("target")?.id;
      const sourceOnLevel = elementIDs.has(sourceId);
      const targetOnLevel = elementIDs.has(targetId);

      if (sourceOnLevel && targetOnLevel) {
        linksOnSameLevel.push(link);
      }
    });

    const rankDir = level % 2 === 0 ? "LR" : "TB";
    const isTB = rankDir === "TB";
    // CREATE only supports Regions from left to right in States.
    // Assuming there are no transitions inbetween Regions, they are on same rank,
    // so we use "TB" because all on same rank are routed LR
    DirectedGraph.layout([...elementsOnSameLevel, ...linksOnSameLevel], {
      rankDir: parentIsState ? "TB" : rankDir,
      marginX: 20,
      marginY: 20,
      nodeSep: isTB ? 150 : 100,
      edgeSep: 20,
      rankSep: isTB ? 100 : 150,
      clusterPadding: { top: 1, left: 1, right: 1, bottom: 1 },
      setLinkVertices: true,
      setLabels: true,
      ranker: "network-simplex",
      align: "UL",
      resizeClusters: false,
    });
    let px = parent ? parent.position().x : 0;
    let py = parent ? parent.position().y : 0;

    // There is some magic in StateView regions that I don't understand, but this does the trick
    if (parent instanceof State) {
      px -= 20;
      py += 20;
    }

    linksOnSameLevel.forEach((link) => {
      const vertices = link.vertices();
      if (vertices && vertices.length > 0) {
        const movedVertices = vertices.map((v) => ({
          x: v.x + px,
          y: v.y + py,
        }));
        link.vertices(movedVertices);
      }
    });
    /** These translations are a performance leak
     * I think we can get rid of it once the px py mystery is solved.
     */
    elementsOnSameLevel.forEach((el) => {
      if (el.getEmbeddedCells().length === 0 && px === 0 && py === 0) {
        return;
      }
      const before = beforePositions.get(el.id);
      const after = el.position();
      const dx = after.x - before.x;
      const dy = after.y - before.y;
      // Move el back to old position
      el.position(before.x, before.y);
      // Then move it and its embedded elements together
      el.translate(dx + px, dy + py);
    });

    if (parent instanceof State || parent instanceof Region) {
      // In theory, we don't need fitToChildren for State because it's handled by State.updateRegionSizes
      // already. BUT: Theres seems to be a flaw in fitToChildren+padding vs updateRegionSizes.
      // The effect is that on 1st layout, all regions get their padding and then they get smaller and smaller b/c of updateRegionSizes.
      parent.fitToChildren({
        padding: { top: 40, left: 40, right: 40, bottom: 40 },
        deep: false,
      });
    }
    if (parent instanceof State) {
      const view = parent.findView(context.paper) as StateView;
      view?.fitToContent();
    }

    this.routeLinks(context, elementsOnSameLevel, context.graph.getLinks(), rankDir);
  }

  /** Somehow, "translate" does not move the labels. This may be even due to a bug in this GraphLayouter.
   * Anyhow, this is the workaround!
   */
  private preprocessLabeledNodes(graph: dia.Graph): {
    cell: LabeledNode;
    children: { child: dia.Element; dx: number; dy: number }[];
  }[] {
    const preprocessedLabeledNodes: {
      cell: LabeledNode;
      children: { child: dia.Element; dx: number; dy: number }[];
    }[] = [];
    graph.getElements().forEach((cell) => {
      if (cell instanceof LabeledNode) {
        const parentPos = cell.position();
        const childrenWithOffset = cell
          .getEmbeddedCells()
          .filter((c): c is dia.Element => c.isElement())
          .map((child) => {
            const childPos = child.position();
            return {
              child,
              dx: childPos.x - parentPos.x,
              dy: childPos.y - parentPos.y,
            };
          });
        preprocessedLabeledNodes.push({
          cell,
          children: childrenWithOffset,
        });
      }
    });

    return preprocessedLabeledNodes;
  }

  /**
   * Link routing strategy is as follows:
   * Golden rule: Use "manhattan" router for each link
   * For each node:
   * - process self-links, incoming links and outgoing links separately.
   * - for incoming and outgoing, handle forward  (i.e. from higher to lower rank), backward and "the rest" differently.
   * - collect the links/the necessary anchors on each side of a node
   * -- If ranking direction is TOP->BOTTOM: self transitions go left->left, forward go bottom->top, backward go right->right, "the rest" goes <closest> to <closest>.
   * -- If ranking direction is LEFT->RIGHT: similar (turn left by 90°)
   * - distribute the link achors on each edge, try to "sort" the achors to avoid link crossings
   */
  private routeLinks(context: RappidContext, elements: dia.Element[], links: dia.Link[], rankDir: string) {
    const elementLinks: {
      element: dia.Element;
      self: dia.Link[];
      incoming: {
        forward: dia.Link[];
        backward: dia.Link[];
      };
      outgoing: {
        forward: dia.Link[];
        backward: dia.Link[];
      };
    }[] = elements.map((el) => {
      const selfLinks: dia.Link[] = [];
      const incomingForward: dia.Link[] = [];
      const incomingBackward: dia.Link[] = [];
      const outgoingForward: dia.Link[] = [];
      const outgoingBackward: dia.Link[] = [];
      const isTB = rankDir === "TB";
      for (const l of links) {
        const sourceId = l.get("source")?.id;
        const targetId = l.get("target")?.id;
        if (sourceId !== el.id && targetId !== el.id) {
          continue; // link is not related to this element
        }
        const sourceCell = l.getSourceCell();
        const targetCell = l.getTargetCell();
        const sourceBBox = sourceCell.getBBox();
        const targetBBox = targetCell.getBBox();
        let isForward = this.isForwardLink(isTB, sourceBBox, targetBBox);
        let isBackward = this.isBackwardLink(isTB, sourceBBox, targetBBox);
        if (!isForward && !isBackward) {
          const inferred = this.inferDirectionForUnknown(isTB, this.getRelevantPoint(l, true), el.getBBox());
          isForward = inferred;
          isBackward = !inferred;
        }
        if (sourceId === el.id && targetId === el.id) {
          selfLinks.push(l);
        } else if (targetId === el.id) {
          if (isForward) {
            incomingForward.push(l);
          } else {
            incomingBackward.push(l);
          }
        } else if (sourceId === el.id) {
          if (isForward) {
            outgoingForward.push(l);
          } else {
            outgoingBackward.push(l);
          }
        }
      }
      return {
        element: el,
        self: selfLinks,
        incoming: {
          forward: incomingForward,
          backward: incomingBackward,
        },
        outgoing: {
          forward: outgoingForward,
          backward: outgoingBackward,
        },
      };
    });
    elementLinks.forEach(({ element, self, incoming, outgoing }) => {
      this.routeSelfLinks(context, element, self, rankDir);
      this.routeIncomingLinks(context, element, incoming, outgoing, rankDir);
      this.routeOutgoingLinks(context, element, incoming, outgoing, rankDir);
    });
  }
  routeOutgoingLinks(
    context: RappidContext,
    element: dia.Element<dia.Element.Attributes, dia.ModelSetOptions>,
    incoming: {
      forward: dia.Link[];
      backward: dia.Link[];
    },
    outgoing: {
      forward: dia.Link[];
      backward: dia.Link[];
    },
    rankDir: string
  ) {
    const isTB = rankDir === "TB";

    let count = 0;
    let howMany = outgoing.forward.length;
    const eBBox = element.getBBox();
    const anchorArgsArray = this.calculateAnchorOffsets(howMany, isTB, eBBox);

    const sortedOutgoingForward = outgoing.forward.sort(this.sortDirectedLinks(isTB, false, false));

    this.orderAnchorArgsForMinimalDist(anchorArgsArray, sortedOutgoingForward, element, false);
    sortedOutgoingForward.forEach((l, idx) => {
      const { dx, dy } = anchorArgsArray[idx];
      l.source({
        id: element.id,
        anchor: {
          name: isTB ? "bottom" : "right",
          args: { dx: dx, dy: dy },
        },
      });
      this.adjustLabelPositionForDirected(l, context, sortedOutgoingForward.length, count, isTB);
      l.vertices([]);
      l.router({
        name: "manhattan",
        args: {
          step: 10,
          padding: 20,
          startDirections: isTB ? ["bottom"] : ["right"],
          endDirections: isTB ? ["top"] : ["left"],
        },
      });
    });

    count = 0;
    howMany = outgoing.backward.length + incoming.backward.length;
    outgoing.backward
      .sort(this.sortDirectedLinks(isTB, false, true))
      .reverse()
      .forEach((l) => {
        count++;
        const anchorOffsetFactor = count / (howMany + 1);
        const borderLength = isTB ? eBBox.height : eBBox.width;
        const offset = (anchorOffsetFactor - 0.5) * borderLength;
        const dx = isTB ? 0 : offset;
        const dy = isTB ? offset : 0;
        const anchorName = isTB ? "right" : "bottom";
        l.source({
          id: element.id,
          anchor: { name: anchorName, args: { dx: dx, dy: dy } },
        });
        this.adjustLabelPositionsForBackward(l, context, isTB);
        const padding = count * 30;
        // Remove only "not guiding" vertices, not all. Vertices are considered "guiding",
        // if they are "farer away" than padding.
        const vertices = l.vertices() || [];
        if (vertices.length > 0) {
          const sourceBBox = element.getBBox();
          const targetBBox = l.getTargetCell().getBBox();
          const filteredVertices = vertices.filter((v) => {
            if (isTB) {
              const maxX = padding + Math.max(sourceBBox.x + sourceBBox.width, targetBBox.x + targetBBox.width);
              return v.x > maxX;
            } else {
              const maxY = padding + Math.max(sourceBBox.y + sourceBBox.height, targetBBox.y + targetBBox.height);
              return v.y > maxY;
            }
          });
          l.vertices(filteredVertices);
        }
        l.router({
          name: "manhattan",
          args: {
            step: 10,
            padding: padding,
            startDirections: [anchorName],
            endDirections: [anchorName],
          },
        });
        l.connector("rounded");
      });
  }
  private adjustLabelPositionsForBackward(
    l: dia.Link<dia.Link.Attributes, dia.ModelSetOptions>,
    context: RappidContext,
    isTB: boolean
  ) {
    if (l.label(0) && l.label(0).attrs?.text?.text) {
      const lBBox = (
        (context.paper.findViewByModel(l) as dia.LinkView).findLabelNode(0) as SVGGraphicsElement
      ).getBBox();
      const distance = 0.97;
      l.label(0).position = {
        distance: distance,
        offset: {
          // adding two pixels to avoid text overlapping with link end triangle
          x: lBBox.width / 2 + (isTB ? 0 : 2),
          y: -lBBox.height / 2 - (isTB ? 0 : 2),
        },
      };
    }
  }

  private adjustLabelPositionForDirected(
    l: dia.Link<dia.Link.Attributes, dia.ModelSetOptions>,
    context: RappidContext,
    total: number,
    count: number,
    isTB: boolean
  ) {
    if (l.label(0) && l.label(0).attrs?.text?.text) {
      const lBBox = (
        (context.paper.findViewByModel(l) as dia.LinkView).findLabelNode(0) as SVGGraphicsElement
      ).getBBox();
      const distance = total ? 0.5 : +((count - 1) * 0.999) / total;
      l.label(0).position = {
        distance: distance,
        offset: {
          x: (isTB ? lBBox.width : 0) / 2,
          y: -lBBox.height / 2,
        },
      };
    }
  }

  routeIncomingLinks(
    context: RappidContext,
    element: dia.Element<dia.Element.Attributes, dia.ModelSetOptions>,
    incoming: {
      forward: dia.Link[];
      backward: dia.Link[];
    },
    outgoing: {
      forward: dia.Link[];
      backward: dia.Link[];
    },
    rankDir: string
  ) {
    const isTB = rankDir === "TB";

    let count = 0;
    let howMany = incoming.forward.length;
    const eBBox = element.getBBox();
    const anchorArgsArray = this.calculateAnchorOffsets(howMany, isTB, eBBox);
    const sortedIncomingForward = incoming.forward.sort(this.sortDirectedLinks(isTB, true, false));

    this.orderAnchorArgsForMinimalDist(anchorArgsArray, sortedIncomingForward, element, true);
    sortedIncomingForward.forEach((l, idx) => {
      const { dx, dy } = anchorArgsArray[idx];
      l.target({
        id: element.id,
        anchor: {
          name: isTB ? "top" : "left",
          args: { dx: dx, dy: dy },
        },
      });
    });
    count = 0;
    howMany = incoming.backward.length + outgoing.backward.length;
    incoming.backward
      .sort(this.sortDirectedLinks(isTB, true, false))
      .reverse()
      .forEach((l) => {
        count++;
        const anchorOffsetFactor = (count + outgoing.backward.length) / (howMany + 1);
        const borderLength = isTB ? eBBox.height : eBBox.width;
        const offset = (anchorOffsetFactor - 0.5) * borderLength;
        const dx = isTB ? 0 : offset;
        const dy = isTB ? offset : 0;
        const anchorName = isTB ? "right" : "bottom";
        l.target({
          id: element.id,
          anchor: { name: anchorName, args: { dx: dx, dy: dy } },
        });
      });
  }

  /** Order anchorArgsArray to minimize the sum of anchor dists to opposite link end. */
  private orderAnchorArgsForMinimalDist(
    anchorArgsArray: { dx: number; dy: number }[],
    sortedLinks: dia.Link<dia.Link.Attributes, dia.ModelSetOptions>[],
    element: dia.Element<dia.Element.Attributes, dia.ModelSetOptions>,
    isIncoming: boolean
  ) {
    if (anchorArgsArray.length === sortedLinks.length && anchorArgsArray.length > 1) {
      // Use a simple brute-force permutation for small arrays (<=6), otherwise use greedy assignment
      const n = anchorArgsArray.length;
      let bestOrder = anchorArgsArray.slice();
      let minSum = Infinity;

      if (n <= 6) {
        // Brute-force all permutations
        function permute(
          arr: { dx: number; dy: number }[],
          l: number,
          cb: (perm: { dx: number; dy: number }[]) => void
        ) {
          if (l === arr.length - 1) {
            cb(arr.slice());
            return;
          }
          for (let i = l; i < arr.length; i++) {
            [arr[l], arr[i]] = [arr[i], arr[l]];
            permute(arr, l + 1, cb);
            [arr[l], arr[i]] = [arr[i], arr[l]];
          }
        }

        permute(anchorArgsArray, 0, (perm) => {
          let sum = 0;
          const pairingKey = [];

          for (let i = 0; i < n; i++) {
            const link = sortedLinks[i];
            const oppositeCell = isIncoming ? link.getSourceCell() : link.getTargetCell();
            const oppositeCenter = oppositeCell.getBBox().center();
            const elementCenter = element.getBBox().center();
            sum += this.anchorDist(perm[i], oppositeCenter, elementCenter);

            // Secondary key for tie-breaking: link ID or position
            pairingKey.push(link.id);
          }

          if (sum < minSum) {
            minSum = sum;
            bestOrder = perm.slice();
          } else if (sum === minSum) {
            // Order must be stable for equal ranks to avoid crossings
            // Tie-breaker: compare pairingKey lexicographically
            const currentKey = pairingKey.join(",");

            const bestKey = sortedLinks.map((l) => l.id.toString()).join(",");
            if (currentKey < bestKey) {
              bestOrder = perm.slice();
            }
          }
        });
      } else {
        // Greedy assignment for larger arrays
        const used = new Array(n).fill(false);
        bestOrder = [];
        for (let i = 0; i < n; i++) {
          let minDist = Infinity;
          let minIdx = -1;
          const sourceCenter = sortedLinks[i].getSourceCell().getBBox().center();
          const elementCenter = element.getBBox().center();
          for (let j = 0; j < n; j++) {
            if (!used[j]) {
              const dist = this.anchorDist(anchorArgsArray[j], sourceCenter, elementCenter);
              if (dist < minDist) {
                minDist = dist;
                minIdx = j;
              }
            }
          }
          used[minIdx] = true;
          bestOrder.push(anchorArgsArray[minIdx]);
        }
      }
      // Replace anchorArgsArray with bestOrder
      anchorArgsArray.length = 0;
      bestOrder.forEach((a) => anchorArgsArray.push(a));
    }
  }

  /* Self transitions all on same edge, but "concentric", i.e. increase dx/dy and padding */
  routeSelfLinks(
    context: RappidContext,
    element: dia.Element<dia.Element.Attributes, dia.ModelSetOptions>,
    selfLinks: dia.Link<dia.Link.Attributes, dia.ModelSetOptions>[],
    rankDir: string
  ) {
    const isTB = rankDir === "TB";
    const anchorName = isTB ? "left" : "top";
    const elementBBox = element.getBBox();
    const borderLength = isTB ? elementBBox.height : elementBBox.width;
    let count = 0;
    selfLinks.forEach((l) => {
      count++;
      const delta = (count * borderLength) / (2 * (count + 1));
      const dy = isTB ? delta : 0;
      const dx = isTB ? 0 : delta;
      l.source({
        id: element.id,
        anchor: { name: anchorName, args: { dy: dy, dx: dx } },
      });
      l.target({
        id: element.id,
        anchor: { name: anchorName, args: { dy: -dy, dx: -dx } },
      });
      this.adjustLabelPositionsForSelfLink(l, context, selfLinks.length, count, isTB);
      l.router({
        name: "manhattan",
        args: {
          step: 10,
          padding: 30 * count,
          startDirections: [anchorName],
          endDirections: [anchorName],
        },
      });
      l.vertices([]);
    });
  }
  private adjustLabelPositionsForSelfLink(
    l: dia.Link,
    context: RappidContext,
    total: number,
    count: number,
    isTB: boolean
  ) {
    if (l.label(0) && l.label(0).attrs?.text?.text) {
      const lBBox = (
        (context.paper.findViewByModel(l) as dia.LinkView).findLabelNode(0) as SVGGraphicsElement
      ).getBBox();
      // distance is a linear distribution along the edge of the element
      // Use 0.999 to keep distance < 1, so that distance is considered as a percentage, not pixel
      const distance = total === 1 ? 0.5 : +((count - 1) * 0.999) / (total - 1);

      const labelOffset_tbx = isTB ? lBBox.width : lBBox.height;
      const labelOffset_tby = isTB ? lBBox.height : lBBox.width;
      // In theory, the label offset linear distribed, too. For some reason, it looked ugly.
      // I played around with the values and found that this works well.
      const offset_tbx = -labelOffset_tbx / 2 - 30 * (count - 1);
      const offset_tby = ((0.9 - 1.3 * distance) * labelOffset_tby) / 2;
      l.label(0).position = {
        distance: distance,
        offset: {
          x: isTB ? offset_tbx : offset_tby,
          y: isTB ? offset_tby : offset_tbx,
        },
      };
    }
  }

  /** Distributes the offsets for link anchors for one node (precicely: for one side of a node).  */
  private calculateAnchorOffsets(count: number, isTB: boolean, bbox: joint.g.Rect): { dx: number; dy: number }[] {
    const offsets: { dx: number; dy: number }[] = [];
    for (let i = 1; i <= count; i++) {
      const anchorOffsetFactor = i / (count + 1);
      const borderLength = isTB ? bbox.width : bbox.height;
      const offset = (anchorOffsetFactor - 0.5) * borderLength;
      const dx = isTB ? offset : 0;
      const dy = isTB ? 0 : offset;
      offsets.push({ dx, dy });
    }
    return offsets;
  }

  private isBackwardLink(isTB: boolean, sourceBBox: joint.g.Rect, targetBBox: joint.g.Rect) {
    return (
      (isTB && sourceBBox.y > targetBBox.y + targetBBox.height) ||
      (!isTB && sourceBBox.x > targetBBox.x + targetBBox.width)
    );
  }

  private isForwardLink(isTB: boolean, sourceBBox: joint.g.Rect, targetBBox: joint.g.Rect) {
    return (
      (isTB && sourceBBox.y + sourceBBox.height < targetBBox.y) ||
      (!isTB && sourceBBox.x + sourceBBox.width < targetBBox.x)
    );
  }

  private inferDirectionForUnknown(isTB: boolean, lastBend: joint.g.Point, bbox: joint.g.Rect): boolean {
    if (isTB) {
      return lastBend.y < bbox.y;
    } else {
      return lastBend.x < bbox.x;
    }
  }

  private anchorDist(args: { dx: number; dy: number }, targetPoint: joint.g.Point, elementCenter: joint.g.Point) {
    const anchorPoint = {
      x: elementCenter.x + args.dx,
      y: elementCenter.y + args.dy,
    };
    const dist = Math.hypot(anchorPoint.x - targetPoint.x, anchorPoint.y - targetPoint.y);
    return dist;
  }

  private getRelevantPoint(link: dia.Link, isIncoming: boolean): joint.g.Point {
    const cell = isIncoming ? link.getSourceCell() : link.getTargetCell();
    return cell.getBBox().center();
  }

  private sortLinksByRelevantPoint(
    isTB: boolean,
    isIncoming: boolean,
    reverseSameDist: boolean,
    links: dia.Link[]
  ): dia.Link[] {
    return links.slice().sort((a, b) => {
      const aPoint = this.getRelevantPoint(a, isIncoming);
      const bPoint = this.getRelevantPoint(b, isIncoming);
      const diff = isTB ? aPoint.y - bPoint.y : aPoint.x - bPoint.x;

      if (diff !== 0) return diff;

      return (reverseSameDist ? -1 : 1) * String(a.id).localeCompare(String(b.id));
    });
  }

  private sortDirectedLinks(
    isTB: boolean,
    isIncoming: boolean,
    reverseSameDist: boolean
  ): (a: dia.Link, b: dia.Link) => number {
    return (a, b) => {
      const sorted = this.sortLinksByRelevantPoint(isTB, isIncoming, reverseSameDist, [a, b]);
      return sorted[0] === a ? -1 : 1;
    };
  }
}

function mapToDebugLabel(cell: dia.Cell): string {
  if (cell instanceof State) {
    return "State " + cell.getName();
  } else if (cell instanceof Entry) {
    return "Entry";
  } else if (cell instanceof Region) {
    return "Region " + cell.getName();
  } else if (cell instanceof BaseNode) {
    return "Node";
  } else if (cell instanceof Transition) {
    return (
      "Transition " +
      cell.getSpecification() +
      mapToDebugLabel(cell.getSourceCell()) +
      "->" +
      mapToDebugLabel(cell.getTargetCell())
    );
  } else {
    return cell.id.toString() + " " + String(cell);
  }
}

function logJointHierarchy(context: RappidContext) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const toJson = (cell: joint.dia.Cell): any => {
    const label = mapToDebugLabel(cell);
    const pos = cell.position();
    const width = cell.attributes?.size?.width;
    const height = cell.attributes?.size?.height;

    const children = cell.getEmbeddedCells().map(toJson);
    return {
      id: cell.id,
      label,
      pos: pos.x + ":" + pos.y + "-" + width + "x" + height,
      children: children.length ? children : undefined,
    };
  };

  const topLevelCells = context.graph.getCells().filter((cell) => !cell.getParentCell());
  const hierarchy = topLevelCells.map(toJson);
}
